import { useMemo } from 'react';
import { CallRecordingSetting } from '@weave/schema-gen-ts/dist/schemas/phone/call-config/v2/call_config.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { createJSONStorage } from 'zustand/middleware';
import { CallIntelStore } from '@frontend/api-call-intel';
import { CallRecordingApi } from '@frontend/api-call-recording';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { createStoreWithPersist } from '@frontend/store';
import { Text, TextLink } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';

const callRecordingsSettingPage = '/phone/call-recording';

type BannerConfig = {
  dismissed: boolean;
  lastSetting?: CallRecordingSetting;
};

// Added this store to manage state of banner via local storage
// This will be easier to trace instead of directly using local storage in the component
const useCallIntelRecordingAlertStore = createStoreWithPersist<{
  bannerConfig: BannerConfig;
  setBannerConfig: (config: BannerConfig) => void;
}>(
  (set) => ({
    bannerConfig: {
      dismissed: false,
      lastSetting: undefined,
    },
    setBannerConfig: (config: BannerConfig) => {
      set({ bannerConfig: config });
    },
  }),
  {
    name: 'call-intel-recording-alert',
    storage: createJSONStorage(() => localStorage),
  },
  {
    name: 'CallIntelRecordingAlert',
    enabled: true,
  }
);

export const CallRecordingsBanner = () => {
  const { t } = useTranslation('analytics');
  const { navigate } = useSettingsNavigate();
  const { isDemoAccount } = CallIntelStore.useCallIntelDemoShallowStore('isDemoAccount');

  const { accessibleLocationData, selectedOrgId } = useAppScopeStore();

  const { bannerConfig: savedBannerConfig, setBannerConfig: setSavedBannerConfig } = useCallIntelRecordingAlertStore();

  const location = accessibleLocationData[selectedOrgId];
  const tenantId = location?.phoneTenantId || '';
  const hasPhoneFullAccess = hasSchemaACL(tenantId, Permission.PHONE_FULL_ACCESS);

  const callRecordingsQuery = useScopedQuery({
    queryKey: queryKeys.listCallRecording(tenantId),
    queryFn: () => CallRecordingApi.GetCallRecording({ tenantId: tenantId }),
  });

  const setting = callRecordingsQuery.data?.setting;

  const showBanner = useMemo(() => {
    if (!setting || setting === CallRecordingSetting.BOTH || setting === CallRecordingSetting.UNSPECIFIED) {
      return false;
    }
    // Show the banner if setting has changed
    if (savedBannerConfig.lastSetting !== setting) {
      setSavedBannerConfig({ dismissed: false, lastSetting: setting });
      return true;
    }
    // Always show the banner if the setting is NONE
    if (setting === CallRecordingSetting.NONE) {
      return true;
    }
    // Show the banner if it hasn't been dismissed
    return !savedBannerConfig.dismissed;
  }, [setting, savedBannerConfig]);

  const dismissBanner = () => {
    setSavedBannerConfig({ dismissed: true, lastSetting: setting });
  };

  if (isDemoAccount || !showBanner) {
    return null;
  }

  if (setting === CallRecordingSetting.NONE) {
    return (
      <GlobalBannerDynamic
        type='error'
        title={t('Call Recordings are Disabled')}
        id='call-intel-recordings-disabled-banner'
        message={t(
          'Calls are not being transcribed or analyzed in Call Intelligence. Enable Call Recordings to resume call transcription and analysis.'
        )}
        trackingId='manage-call-recording-action'
        isDismissible={false}
        action={
          hasPhoneFullAccess
            ? {
                label: t('Manage Call Recording'),
                onClick: () => {
                  navigate({ to: callRecordingsSettingPage });
                },
              }
            : undefined
        }
        priority={10} //  setting higher priority to show this before other banners
      />
    );
  } else if (setting && [CallRecordingSetting.INBOUND, CallRecordingSetting.OUTBOUND].includes(setting)) {
    const callDirection = setting === CallRecordingSetting.INBOUND ? 'inbound' : 'outbound';
    const bannerTitle =
      setting === CallRecordingSetting.INBOUND ? t('Recording Inbound Calls Only') : t('Recording Outbound Calls Only');
    return (
      <GlobalBannerDynamic
        type='info'
        title={bannerTitle}
        id='call-intel-recordings-disabled-banner'
        message={
          <Text as='span' size='medium'>
            <Trans t={t} callDirection={callDirection}>
              Your{' '}
              <TextLink
                onClick={() => {
                  navigate({ to: callRecordingsSettingPage });
                }}
              >
                Call Recording settings
              </TextLink>{' '}
              are set to only record {{ callDirection }} calls. Call Intelligence will now only transcribe and analyze{' '}
              {{ callDirection }} calls
            </Trans>
          </Text>
        }
        isDismissible
        onDismiss={dismissBanner}
      />
    );
  }

  return null;
};
